import React, { useState, useEffect, useRef } from 'react';
import { useT } from '../lib/i18n';
import { payPromptPay, payUploadSlip, payPromptPayStatus, type PayItem, type PromptPayCreated } from '../services/api';
import { qrToSvg } from '../vendor/qrcode';

type Phase = 'loading' | 'qr' | 'pending' | 'approved' | 'rejected' | 'error';

// Shared PromptPay checkout modal — used for both membership plans and course
// purchases. Creates a pending payment, shows the dynamic QR (amount embedded),
// takes a slip upload, then polls until an admin approves or rejects.
export function PromptPayCheckout({ kind, item, title, onClose, onDone }: {
  kind: 'plan' | 'course' | 'ea' | 'ea_bundle';
  item: PayItem;
  title?: string;
  onClose: () => void;
  onDone?: () => void;
}) {
  const t = useT();
  const [phase, setPhase] = useState<Phase>('loading');
  const [data, setData] = useState<PromptPayCreated | null>(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState('');
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // Still on screen? startPolling is reached from an async callback - after the
  // slip upload resolves - so a customer who closes the modal mid-upload
  // unmounts the component, runs the cleanup (which finds no interval yet), and
  // only THEN gets an interval created, with nothing left to clear it. That one
  // polls /api/pay/status every four seconds for the life of the tab.
  const mounted = useRef(true);
  useEffect(() => () => { mounted.current = false; }, []);

  const errText = (code?: string) => {
    if (code === 'no_promptpay') return t('ร้านยังไม่ได้ตั้งค่าพร้อมเพย์ (PROMPTPAY_ID)', 'PromptPay is not configured on the server (PROMPTPAY_ID)');
    if (code === 'login_required') return t('กรุณาเข้าสู่ระบบก่อนชำระเงิน', 'Please sign in before paying');
    if (code === 'bad_item') return t('รายการสินค้าไม่ถูกต้อง', 'Invalid item');
    if (code === 'already_owned') return t('คุณเป็นเจ้าของรายการนี้อยู่แล้ว — ดาวน์โหลดได้เลยจากการ์ด', 'You already own this — download it from the card');
    if (code === 'already_enrolled') return t('คุณชำระค่าคลาสนี้ไว้แล้ว — เข้าเรียนได้เลยจากหน้าคอร์สเรียน ไม่ต้องจ่ายซ้ำ ถ้ายังเปิดไม่ได้ลองรีเฟรชหน้าหรือทักแอดมิน',
                                              'You have already paid for this class — open it from the Academy page, no need to pay again. If it will not open, refresh or message us.');
    if (code === 'already_paid') return t('รายการนี้เพิ่งชำระและอนุมัติไปแล้ววันนี้ — ไม่ต้องจ่ายซ้ำ สิทธิ์เปิดให้แล้ว ถ้ายังใช้ไม่ได้ทักแอดมินได้เลย',
                                          'You paid for this today and it is already approved — no need to pay again. If access is still missing, message us.');
    return t('เกิดข้อผิดพลาด: ', 'Something went wrong: ') + (code || '');
  };

  // Create the pending payment on open.
  useEffect(() => {
    let alive = true;
    (async () => {
      const r = await payPromptPay(kind, item);
      if (!alive) return;
      if (r.free || r.enrolled) { setPhase('approved'); onDone?.(); return; }
      if (r.error || !r.payload) { setErr(errText(r.error)); setPhase('error'); return; }
      setData(r);
      // A reopened order whose slip is already on file goes straight to the
      // waiting screen and starts watching for the approval. It used to stay
      // on the QR with "Attach slip" and never poll, so customers attached the
      // slip again or paid a second time, and the approval never reached the
      // open page.
      if (r.reused && r.slip && r.ref) { setPhase('pending'); startPolling(r.ref); return; }
      setPhase('qr');
      // Reopening the same order rather than making a new one is the fix for
      // the duplicate rows, but silently showing the same QR would look like
      // nothing happened - and a customer who already sent a slip needs to be
      // told it is on file, or they will send it again.
      if (r.reused) {
        setNote(r.slip
          ? t('นี่คือรายการเดิมที่คุณเปิดไว้ และแนบสลิปแล้ว — รอแอดมินตรวจสอบ ไม่ต้องโอนซ้ำ',
              'This is the order you already opened, and your slip is attached — waiting for review. Do not transfer again.')
          : t('นี่คือรายการเดิมที่คุณเปิดค้างไว้ ใช้ QR เดิมได้เลย ไม่ได้สร้างรายการใหม่',
              'This is the order you already had open — same QR, no new order was created.'));
      }
    })();
    return () => { alive = false; if (pollRef.current) clearInterval(pollRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startPolling = (ref: string) => {
    if (pollRef.current) clearInterval(pollRef.current);
    if (!mounted.current) return;          // the modal is already gone
    pollRef.current = setInterval(async () => {
      const s = await payPromptPayStatus(ref);
      // The await can also outlive the modal, so re-check before touching state.
      if (!mounted.current) { if (pollRef.current) clearInterval(pollRef.current); return; }
      if (s.status === 'approved') { if (pollRef.current) clearInterval(pollRef.current); setPhase('approved'); onDone?.(); }
      else if (s.status === 'rejected') { if (pollRef.current) clearInterval(pollRef.current); setPhase('rejected'); }
    }, 4000);
  };

  const onPickSlip = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    // Clear the input straight away: after a rejected or failed upload,
    // re-picking the SAME file fires no change event and the button looks dead.
    e.target.value = '';
    if (!file || !data?.ref) return;
    if (file.size > 5 * 1024 * 1024) { setErr(t('ไฟล์ใหญ่เกิน 5MB', 'File exceeds 5MB')); return; }
    setErr(''); setBusy(true);
    const reader = new FileReader();
    reader.onload = async () => {
      const r = await payUploadSlip(data.ref!, String(reader.result), file.name);
      setBusy(false);
      if (r.error) { setErr(t('อัปโหลดสลิปไม่สำเร็จ: ', 'Slip upload failed: ') + r.error); return; }
      setPhase('pending');
      startPolling(data.ref!);
    };
    reader.onerror = () => { setBusy(false); setErr(t('อ่านไฟล์ไม่สำเร็จ', 'Could not read the file')); };
    reader.readAsDataURL(file);
  };

  const baht = data?.amount ? Math.round(data.amount / 100).toLocaleString() : '';

  return (
    <div className="mm-modal-bg" onClick={onClose}>
      <div className="mm-modal glass mm-pp" onClick={e => e.stopPropagation()}>
        <button className="mm-modal-x" onClick={onClose}>✕</button>

        {phase === 'loading' && <p className="dim" style={{ padding: '32px 0', textAlign: 'center' }}>{t('กำลังสร้าง QR…', 'Creating QR…')}</p>}

        {phase === 'error' && (
          <div style={{ textAlign: 'center', padding: '16px 0' }}>
            <h2>{t('ชำระเงินไม่ได้', 'Cannot start payment')}</h2>
            <div className="mm-err" style={{ marginTop: 12 }}>{err}</div>
          </div>
        )}

        {phase === 'qr' && data && (
          <React.Fragment>
            <h2 style={{ textAlign: 'center' }}>{title || t('สแกนจ่ายด้วยพร้อมเพย์', 'Pay with PromptPay')}</h2>
            <p className="dim" style={{ textAlign: 'center', marginTop: -4 }}>{data.label}</p>
            {note && (
              <p className="mm-pp-note">{note}</p>
            )}
            <div className="mm-pp-qr" dangerouslySetInnerHTML={{ __html: qrToSvg(data.payload!, { size: 220, margin: 2, dark: '#0b1220', light: '#ffffff' }) }} />
            <div className="mm-pp-amt">
              <span className="dim">{t('จำนวนเงิน', 'Amount')}</span>
              <strong className="mono">฿{baht}</strong>
            </div>
            {data.name && <p className="dim mono" style={{ textAlign: 'center', fontSize: 12 }}>{data.name}</p>}
            <p className="mono dim" style={{ textAlign: 'center', fontSize: 12 }}>{t('อ้างอิง', 'Ref')} {data.ref}</p>
            <ol className="mm-pp-steps">
              <li>{t('สแกน QR ด้วยแอปธนาคาร แล้วโอนตามยอด', 'Scan the QR in your banking app and transfer the amount')}</li>
              <li>{t('ถ่าย/บันทึกสลิป แล้วแนบด้านล่าง', 'Save your slip and attach it below')}</li>
              <li>{t('รอแอดมินยืนยัน ระบบจะปลดล็อกให้อัตโนมัติ', 'Wait for admin approval — access unlocks automatically')}</li>
            </ol>
            {err && <div className="mm-err">{err}</div>}
            <label className="mm-btn primary big w100" style={{ cursor: 'pointer', textAlign: 'center' }}>
              {busy ? t('กำลังอัปโหลด…', 'Uploading…') : t('📎 แนบสลิปโอนเงิน', '📎 Attach transfer slip')}
              <input type="file" accept="image/*" hidden disabled={busy} onChange={onPickSlip} />
            </label>
          </React.Fragment>
        )}

        {phase === 'pending' && (
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <div className="mm-pp-spin">⏳</div>
            <h2>{t('รอแอดมินยืนยัน', 'Waiting for approval')}</h2>
            <p className="dim">{t('ได้รับสลิปแล้ว — กำลังตรวจสอบการโอน ระบบจะปลดล็อกให้อัตโนมัติเมื่อยืนยัน คุณปิดหน้านี้ได้', 'Slip received — we are verifying the transfer. Access unlocks automatically once approved. You may close this window.')}</p>
            <p className="mono dim" style={{ fontSize: 12 }}>{t('อ้างอิง', 'Ref')} {data?.ref}</p>
          </div>
        )}

        {phase === 'approved' && (
          <div className="mm-upgrade-done" style={{ textAlign: 'center', padding: '20px 0' }}>
            <span className="mm-upgrade-check">✓</span>
            <h2>{t('ชำระเงินสำเร็จ', 'Payment confirmed')}</h2>
            <p className="dim">{kind === 'plan' ? t('ปลดล็อกทุกฟีเจอร์แล้ว 🐯', 'All features unlocked 🐯')
              : kind === 'ea' ? t('EA เป็นของคุณแล้ว — ปุ่มดาวน์โหลดเปิดให้ทันที 🤖', 'The EA is yours — the download button is now unlocked 🤖')
              : kind === 'ea_bundle' ? t('EA ทุกตัวเป็นของคุณแล้ว — ดาวน์โหลดได้ทั้งชุด 🤖', 'Every EA is yours — the whole collection is unlocked 🤖')
              : t('เพิ่มคอร์สให้แล้ว เริ่มเรียนได้เลย 🎓', 'Course added — start learning 🎓')}</p>
            <button className="mm-btn primary" style={{ marginTop: 12 }} onClick={onClose}>{t('เยี่ยม!', 'Great!')}</button>
          </div>
        )}

        {phase === 'rejected' && (
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <h2>{t('สลิปไม่ผ่านการตรวจสอบ', 'Slip was not approved')}</h2>
            <p className="dim">{t('ยอดโอนอาจไม่ตรง หรือสลิปไม่ชัด กรุณาลองใหม่หรือติดต่อแอดมิน', 'The amount may not match or the slip was unclear. Please try again or contact admin.')}</p>
            <button className="mm-btn ghost" style={{ marginTop: 12 }} onClick={onClose}>{t('ปิด', 'Close')}</button>
          </div>
        )}
      </div>
    </div>
  );
}
